import {
    ImageGalleryEnrichedImage,
    ImageShape,
    ImageTemplate,
    ImageTemplateMatchResult,
} from '../../ImageGallery.types';
import { IMAGE_MATCHING_CONFIG } from '../ImageGalleryGrid.constants';
import { matchTemplates } from './matchTemplates';

/**
 * Helper to calculate counts of each image shape.
 */
function getShapeCounts(images: ImageGalleryEnrichedImage[]): Record<ImageShape, number> {
    const counts: Record<ImageShape, number> = {
        square: 0,
        portrait: 0,
        landscape: 0,
        banner: 0,
    };
    images.forEach((img) => {
        counts[img.shape] = (counts[img.shape] ?? 0) + 1;
    });
    return counts;
}

/**
 * Adjusts templates priorities dynamically based on shape distribution.
 * Templates whose slots better match the prevalent image shapes get a priority boost.
 */
function adjustTemplatesByShapeDistribution(
    templates: ImageTemplate[],
    images: ImageGalleryEnrichedImage[]
): ImageTemplate[] {
    const shapeCounts = getShapeCounts(images);

    return templates.map((template) => {
        const slotShapes = template.slots.map((slot) => slot.shape);
        // Sum counts of images matching slot shapes
        const shapeMatchCount = slotShapes.reduce((sum, shape) => sum + (shapeCounts[shape] ?? 0), 0);
        const priorityBoost = shapeMatchCount / template.slots.length; // normalize by slot count
        return {
            ...template,
            priority: (template.priority ?? 0) + priorityBoost * IMAGE_MATCHING_CONFIG.shapeDistributionPriorityBoost, // weight boost to influence sorting
        };
    });
}

/**
 * Recursively generates an image gallery layout by selecting the best matching templates to cover all images.
 *
 * Attempts to assign images to templates with all slots filled first, and only if no such full match is found,
 * falls back to allowing partial template filling where some slots may remain empty.
 *
 * This fallback helps place leftover images that cannot fill a full template, improving overall image coverage
 * at the cost of some empty slots in the last templates.
 *
 * After each template assignment, updates `recentTemplates` to track usage history and applies a descending
 * recency penalty to encourage template variety throughout the sequence.
 *
 * @param images - Array of enriched images to layout.
 * @param templates - Array of available image templates.
 * @param recentTemplates - Array of recent template IDs to discourage immediate reuse (optional).
 * @param maxRecentTemplates - Maximum number of recent templates to track for recency penalty (default: 3).
 * @returns An array of MatchResult objects representing a sequence of template-matching blocks covering all images,
 * including possibly partially filled templates near the end.
 */
export function generateImageLayout(
    images: ImageGalleryEnrichedImage[],
    templates: ImageTemplate[],
    recentTemplates: string[] = [],
    maxRecentTemplates = 3
): ImageTemplateMatchResult[] {
    if (images.length === 0) return [];

    // Adjust templates by image shape distribution before matching
    const adjustedTemplates = adjustTemplatesByShapeDistribution(templates, images);

    let match = matchTemplates(images, adjustedTemplates, recentTemplates, false);

    if (!match) {
        match = matchTemplates(images, adjustedTemplates, recentTemplates, true);
        if (match?.template.mustFill) {
            match = null;
        }
    }

    if (!match) return [];

    const { template, usedImagesBySlot } = match;

    const usedImages = usedImagesBySlot.filter((img): img is ImageGalleryEnrichedImage => img !== null);
    const remainingImages = images.filter((img) => !usedImages.includes(img));

    const updatedRecentTemplates = [template.id, ...recentTemplates].slice(0, maxRecentTemplates);

    return [match, ...generateImageLayout(remainingImages, templates, updatedRecentTemplates, maxRecentTemplates)];
}
